import { Directive, Input, ElementRef, OnDestroy, OnInit, AfterViewInit } from "@angular/core";
import { SharedService } from "src/app/shared.service";
import {
  PermissionModel,
  AdminPermissionResponseModel
} from "../models/common-request-response";
import { GlobalService } from "../services/global.service";
import { JwtService } from "../services/jwt.service";

/**
 * ACL for hotel-admin UI.
 *
 * Usage:
 *   [appAccessCheck]="'rooms'"
 *   [appAccessCheck]="['Manage-Serivce-Inventory','Approve-Service-Inventory-Requests']"  // OR
 *   [appAccessCheck]="['homepage-menu','about']" [accessRequireAll]="true"              // AND
 *   [givenButton]="'add' | 'edit' | 'save' | 'update' | 'delete' | 'view' | ..."
 *
 * Levels (exclusive in role UI; evaluated independently at runtime):
 *   Full      — everything
 *   Manage    — everything except delete
 *   ViewOnly  — view / cancel / menu visibility only
 */
@Directive({
  selector: "[appAccessCheck]"
})
export class AccessCheckDirective implements OnInit, AfterViewInit, OnDestroy {
  permissionChangeEvent$;

  @Input("appAccessCheck") givenRoute: string | string[];

  @Input() givenButton: string;

  /** When true with multiple NameKeys, every key must allow the action (AND). Default is OR. */
  @Input() accessRequireAll = false;

  private adminPermissionResponseModel: AdminPermissionResponseModel = new AdminPermissionResponseModel();
  public permissionModel: PermissionModel = new PermissionModel();

  /** Buttons that ViewOnly (and higher) may see. */
  private static readonly VIEW_BUTTONS = new Set([
    "view",
    "visibility",
    "show",
    "cancel"
  ]);

  /** Destructive actions — Full only. */
  private static readonly DELETE_BUTTONS = new Set(["delete"]);

  constructor(
    private el: ElementRef,
    private sharedService: SharedService,
    public globalService: GlobalService,
    private jwtService: JwtService
  ) {}

  ngOnInit() {
    this.checkPermissions(null);
  }

  ngAfterViewInit() {
    this.permissionChangeEvent$ = this.sharedService.permissionEvent.subscribe(
      (data: any) => {
        if (!this.globalService.validateIsEmpty(data)) {
          window.localStorage["bulkPermissions"] = JSON.stringify(data);
        }
        // Always evaluate against the raw permissions array (not a single-object deserialize).
        this.checkPermissions(data);
      }
    );
  }

  ngOnDestroy() {
    if (this.permissionChangeEvent$) {
      this.permissionChangeEvent$.unsubscribe();
    }
  }

  private routeKeys(): string[] {
    if (Array.isArray(this.givenRoute)) {
      return this.givenRoute.filter(k => !!k && typeof k === "string");
    }
    if (typeof this.givenRoute === "string" && this.givenRoute.trim()) {
      return this.givenRoute
        .split(",")
        .map(k => k.trim())
        .filter(Boolean);
    }
    return [];
  }

  private loadPermissionList(adminPermissionResponseModel): any[] | null {
    if (Array.isArray(adminPermissionResponseModel) && adminPermissionResponseModel.length) {
      return adminPermissionResponseModel;
    }

    try {
      const adminPermissions = JSON.parse(
        window.localStorage["bulkPermissions"] || "null"
      );
      if (Array.isArray(adminPermissions) && adminPermissions.length) {
        return adminPermissions;
      }
    } catch {
      // ignore corrupt localStorage
    }
    return null;
  }

  private findPermissionForKey(list: any[], nameKey: string): PermissionModel | null {
    const findIndex = list
      .map((item: any) => item?.nameKey || item?.NameKey)
      .indexOf(nameKey);
    if (findIndex === -1 || !list[findIndex]) {
      return null;
    }

    const rawPerms = list[findIndex].permissions || list[findIndex].Permissions;
    if (!rawPerms || !rawPerms[0]) {
      return null;
    }

    const raw = rawPerms[0];
    // Prefer model deserialize, then normalize PascalCase API fields.
    try {
      this.permissionModel = this.globalService.jsonConvert.deserializeObject(
        raw,
        PermissionModel
      );
    } catch {
      this.permissionModel = new PermissionModel();
    }

    return this.normalizePermissionFlags(this.permissionModel, raw);
  }

  private normalizePermissionFlags(model: PermissionModel, raw: any): PermissionModel {
    const full = this.readFlag(model, raw, "full", "Full");
    const manage = this.readFlag(model, raw, "manage", "Manage");
    const viewOnly = this.readFlag(model, raw, "viewOnly", "ViewOnly");
    const bookOnly = this.readFlag(model, raw, "bookOnly", "BookOnly");

    model.full = full;
    model.manage = manage;
    model.viewOnly = viewOnly;
    model.bookOnly = bookOnly;
    return model;
  }

  private readFlag(model: PermissionModel, raw: any, camel: string, pascal: string): boolean {
    const fromModel = model ? (model as any)[camel] : undefined;
    const fromRaw = raw ? raw[camel] ?? raw[pascal] : undefined;
    return !!(fromModel ?? fromRaw);
  }

  checkPermissions(adminPermissionResponseModel) {
    if (!this.jwtService.getUserRole) {
      this.hide();
      return;
    }
    if (this.jwtService.getUserRole() === "admin") {
      return;
    }

    const keys = this.routeKeys();
    if (!keys.length) {
      this.hide();
      return;
    }

    const list = this.loadPermissionList(adminPermissionResponseModel);
    if (!list) {
      // Keep current visibility if permissions are not loaded yet.
      return;
    }

    const permissionResults = keys.map(key => this.findPermissionForKey(list, key));

    if (this.accessRequireAll) {
      if (permissionResults.some(p => !p)) {
        this.hide();
        return;
      }
      const allowed = permissionResults.every(p => this.isActionAllowed(p));
      this.applyVisibility(allowed);
      return;
    }

    // OR: any key that grants the action is enough
    const allowed = permissionResults.some(p => p && this.isActionAllowed(p));
    this.applyVisibility(allowed);
  }

  /**
   * Map Full / Manage / ViewOnly (+ legacy BookOnly for menu) onto the button action.
   */
  private isActionAllowed(permissions: PermissionModel): boolean {
    const full = !!permissions.full;
    const manage = !!permissions.manage;
    const viewOnly = !!permissions.viewOnly;
    const bookOnly = !!permissions.bookOnly;

    const canWrite = full || manage;
    const canView = full || manage || viewOnly || bookOnly;

    // Menu / route visibility (no button)
    if (!this.givenButton) {
      return canView;
    }

    const button = (this.givenButton || "").toLowerCase();

    if (AccessCheckDirective.DELETE_BUTTONS.has(button)) {
      return full;
    }

    if (AccessCheckDirective.VIEW_BUTTONS.has(button)) {
      return canView;
    }

    /* Editing a restaurant card reshapes the offering itself, so it stays with Full. */
    if (button === "restaurant-card-edit") {
      return full;
    }

    // add / edit / save / update / manage / and any other mutating action
    // Manage-only: everything except delete
    return canWrite;
  }

  private applyVisibility(allowed: boolean) {
    if (allowed) {
      // Restore natural layout — never force inline-block (breaks mat-input / checkbox / toggle).
      this.el.nativeElement.style.removeProperty("display");
    } else {
      this.hide();
    }
  }

  private hide() {
    this.el.nativeElement.style.display = "none";
  }

  /** @deprecated kept for any external callers; prefer isActionAllowed */
  permission(permissions) {
    this.applyVisibility(this.isActionAllowed(permissions));
  }
}
